/**
 * Assign-menu targets (SOUND-667) — a ticket can be assigned to a PERSON or to
 * a JOB FUNCTION, and the function row has to say where it will land BEFORE the
 * click. The user should never have to guess who they just handed work to.
 *
 * This file exists because this repo has no component-test harness (SOUND-679):
 * anything left inline in a page body is unverifiable except by a human
 * clicking it, and four defects in the last sprint were exactly that. The
 * derivation and the filtering live here so they can be tested; the component
 * keeps only rendering.
 *
 * ── The resolvability rule MIRRORS THE SERVER ──
 * `sound-api/services/assigneeResolver.mjs` decides who actually gets the
 * ticket. This module predicts that answer for the UI, so the two must agree:
 *
 *   defaultOwner  → the function's defaultOwner, if that user is ACTIVE
 *   leastLoaded   → the ACTIVE holder with the fewest open tickets;
 *                   falls back to the default owner when there are NO active
 *                   holders to balance across
 *
 * If neither yields anyone, the API refuses the assignment with a 400 (there is
 * no queue to fall back to). We render that function DISABLED rather than
 * hiding it — a missing row is a mystery, a disabled row is a diagnosis, and it
 * points the user at /settings/functions to fix it. As of 2026-08-24 no active
 * function in prod is unresolvable, and SOUND-686 stops new ones being created,
 * so this is a defensive path, not an expected one.
 *
 * NOTE `holderCount` from the API counts ALL holders, including deactivated
 * users. Resolvability must count ACTIVE holders only — using holderCount here
 * would show a staffed-looking function that the API then refuses.
 */

/** Active holders of a function. The API's `holders[]` is NOT status-filtered. */
export function activeHolders(fn = {}) {
  return (fn.holders || []).filter((h) => h && h.status === "active");
}

/** A default owner counts only when the user is active — same as the resolver. */
export function hasActiveOwner(fn = {}) {
  return Boolean(fn.defaultOwner && fn.defaultOwner.status === "active");
}

/** The strategy actually in force. Unset means defaultOwner (server default). */
export function strategyOf(fn = {}) {
  return fn.strategy === "leastLoaded" ? "leastLoaded" : "defaultOwner";
}

/**
 * Describe where assigning to this function will land.
 *
 * @returns {{
 *   resolvable: boolean,
 *   via: "owner" | "capacity" | "none",
 *   ownerName: string | null,
 *   activeHolderCount: number,
 *   hint: string,
 * }}
 */
export function describeFunctionTarget(fn = {}) {
  const holders = activeHolders(fn);
  const ownerActive = hasActiveOwner(fn);
  const ownerName = ownerActive ? fn.defaultOwner.displayName || fn.defaultOwner.email : null;

  // Capacity routing needs someone to balance ACROSS; one active holder still
  // counts (the resolver picks least-loaded among them, trivially that person).
  if (strategyOf(fn) === "leastLoaded" && holders.length > 0) {
    return {
      resolvable: true,
      via: "capacity",
      ownerName,
      activeHolderCount: holders.length,
      hint: `Least loaded of ${holders.length}`,
    };
  }

  // Either a defaultOwner function, or a leastLoaded one with nobody to balance
  // across — both resolve through the default owner.
  if (ownerActive) {
    return {
      resolvable: true,
      via: "owner",
      ownerName,
      activeHolderCount: holders.length,
      hint: ownerName,
    };
  }

  return {
    resolvable: false,
    via: "none",
    ownerName: null,
    activeHolderCount: holders.length,
    hint: "No owner set",
  };
}

/** Case-insensitive "contains" over a set of fields, ignoring blanks. */
function matches(query, ...fields) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f && String(f).toLowerCase().includes(q));
}

/**
 * Filter the job-function section by the menu's search box.
 *
 * Matches the LABEL and the functionKey — someone who knows the queue as
 * `mid_applications` should find it typing either. Only ACTIVE functions are
 * offered; an inactive one is not assignable and the API refuses it.
 */
export function filterFunctionTargets(functions = [], query = "") {
  return (functions || [])
    .filter((f) => f && f.active !== false)
    .filter((f) => matches(query, f.label, f.functionKey));
}

/**
 * Filter the people section. The users endpoint already applies `?search=`
 * server-side; this is the client-side pass that keeps BOTH sections consistent
 * when the two lists come from different places.
 */
export function filterUserTargets(users = [], query = "") {
  return (users || []).filter((u) =>
    matches(query, u.name, u.email, [u.firstName, u.lastName].filter(Boolean).join(" "))
  );
}

/**
 * The toast line after a function assignment resolves. Names the PERSON, not
 * the function — "Assigned to Underwriting" would hide who is now on the hook.
 */
export function assignedViaMessage(assignee, functionLabel) {
  const who = assignee?.displayName || assignee?.email || "someone";
  return functionLabel ? `Assigned to ${who} via ${functionLabel}` : `Assigned to ${who}`;
}

/** Resolve a functionKey to its human label, falling back to the raw key. */
export function functionLabel(functions = [], key) {
  if (!key) return null;
  const fn = (functions || []).find((f) => f.functionKey === key);
  return fn?.label || key;
}
